/**
 * Static travel reference data + local (browser-storage) utilities.
 *
 * Live pricing, availability, status and FX now come from the provider layer
 * (`@/lib/travel/service.server` via `/api/travel/*`, consumed through
 * `@/hooks/use-travel-live`). This module keeps only data that is genuinely
 * static (airport catalogue, airline metadata, currency names, airport guides,
 * visa/transit notes) plus the user's saved routes and price alerts.
 */

export type Airport = {
  code: string;
  name: string;
  city: string;
  country: string;
  lat: number;
  lng: number;
};

export type Airline = {
  code: string;
  name: string;
  rating: number; // 0-5
  baggageIncluded: number; // kg included in economy
  extraBagFee: number; // USD per extra bag
};

export const AIRPORTS: Airport[] = [
  {
    code: "LOS",
    name: "Murtala Muhammed Intl",
    city: "Lagos",
    country: "Nigeria",
    lat: 6.58,
    lng: 3.32,
  },
  {
    code: "ABV",
    name: "Nnamdi Azikiwe Intl",
    city: "Abuja",
    country: "Nigeria",
    lat: 9.0,
    lng: 7.26,
  },
  { code: "ACC", name: "Kotoka Intl", city: "Accra", country: "Ghana", lat: 5.6, lng: -0.17 },
  {
    code: "JNB",
    name: "OR Tambo Intl",
    city: "Johannesburg",
    country: "South Africa",
    lat: -26.13,
    lng: 28.24,
  },
  {
    code: "NBO",
    name: "Jomo Kenyatta Intl",
    city: "Nairobi",
    country: "Kenya",
    lat: -1.32,
    lng: 36.93,
  },
  { code: "CAI", name: "Cairo Intl", city: "Cairo", country: "Egypt", lat: 30.11, lng: 31.41 },
  {
    code: "LHR",
    name: "Heathrow",
    city: "London",
    country: "United Kingdom",
    lat: 51.47,
    lng: -0.45,
  },
  {
    code: "LGW",
    name: "Gatwick",
    city: "London",
    country: "United Kingdom",
    lat: 51.15,
    lng: -0.19,
  },
  {
    code: "CDG",
    name: "Charles de Gaulle",
    city: "Paris",
    country: "France",
    lat: 49.01,
    lng: 2.55,
  },
  {
    code: "AMS",
    name: "Schiphol",
    city: "Amsterdam",
    country: "Netherlands",
    lat: 52.31,
    lng: 4.76,
  },
  {
    code: "FRA",
    name: "Frankfurt Am Main",
    city: "Frankfurt",
    country: "Germany",
    lat: 50.04,
    lng: 8.56,
  },
  { code: "IST", name: "Istanbul", city: "Istanbul", country: "Türkiye", lat: 41.28, lng: 28.74 },
  { code: "DXB", name: "Dubai Intl", city: "Dubai", country: "UAE", lat: 25.25, lng: 55.36 },
  { code: "DOH", name: "Hamad Intl", city: "Doha", country: "Qatar", lat: 25.27, lng: 51.61 },
  {
    code: "JFK",
    name: "John F Kennedy Intl",
    city: "New York",
    country: "United States",
    lat: 40.64,
    lng: -73.78,
  },
  {
    code: "EWR",
    name: "Newark Liberty",
    city: "New York",
    country: "United States",
    lat: 40.69,
    lng: -74.17,
  },
  {
    code: "YYZ",
    name: "Toronto Pearson",
    city: "Toronto",
    country: "Canada",
    lat: 43.68,
    lng: -79.63,
  },
  {
    code: "SYD",
    name: "Sydney Kingsford Smith",
    city: "Sydney",
    country: "Australia",
    lat: -33.94,
    lng: 151.18,
  },
  { code: "SIN", name: "Changi", city: "Singapore", country: "Singapore", lat: 1.36, lng: 103.99 },
  { code: "HND", name: "Haneda", city: "Tokyo", country: "Japan", lat: 35.55, lng: 139.78 },
];

export const AIRLINES: Airline[] = [
  { code: "BA", name: "British Airways", rating: 4.1, baggageIncluded: 23, extraBagFee: 75 },
  { code: "AF", name: "Air France", rating: 4.0, baggageIncluded: 23, extraBagFee: 70 },
  { code: "KL", name: "KLM", rating: 4.2, baggageIncluded: 23, extraBagFee: 70 },
  { code: "LH", name: "Lufthansa", rating: 4.2, baggageIncluded: 23, extraBagFee: 80 },
  { code: "TK", name: "Turkish Airlines", rating: 4.4, baggageIncluded: 30, extraBagFee: 60 },
  { code: "EK", name: "Emirates", rating: 4.6, baggageIncluded: 30, extraBagFee: 90 },
  { code: "QR", name: "Qatar Airways", rating: 4.7, baggageIncluded: 30, extraBagFee: 85 },
  { code: "ET", name: "Ethiopian", rating: 4.0, baggageIncluded: 23, extraBagFee: 65 },
  { code: "KQ", name: "Kenya Airways", rating: 3.9, baggageIncluded: 23, extraBagFee: 60 },
  { code: "SA", name: "South African", rating: 3.8, baggageIncluded: 23, extraBagFee: 60 },
];

export const CURRENCIES: { code: string; name: string }[] = [
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "NGN", name: "Nigerian Naira" },
  { code: "GHS", name: "Ghanaian Cedi" },
  { code: "KES", name: "Kenyan Shilling" },
  { code: "ZAR", name: "South Afr. Rand" },
  { code: "AED", name: "UAE Dirham" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "INR", name: "Indian Rupee" },
  { code: "CNY", name: "Chinese Yuan" },
  { code: "JPY", name: "Japanese Yen" },
];

export function findAirport(code: string): Airport | undefined {
  return AIRPORTS.find((a) => a.code.toUpperCase() === code.toUpperCase());
}

export function searchAirports(q: string): Airport[] {
  const s = q.trim().toLowerCase();
  if (!s) return AIRPORTS.slice(0, 8);
  return AIRPORTS.filter(
    (a) =>
      a.code.toLowerCase().includes(s) ||
      a.city.toLowerCase().includes(s) ||
      a.country.toLowerCase().includes(s) ||
      a.name.toLowerCase().includes(s),
  ).slice(0, 12);
}

export function nearbyAirports(code: string, radiusKm = 400): Airport[] {
  const origin = findAirport(code);
  if (!origin) return [];
  return AIRPORTS.filter((a) => a.code !== origin.code)
    .map((a) => {
      const dx = (a.lat - origin.lat) * 111;
      const dy = (a.lng - origin.lng) * 111;
      return { a, d: Math.round(Math.sqrt(dx * dx + dy * dy)) };
    })
    .filter((x) => x.d <= radiusKm)
    .sort((a, b) => a.d - b.d)
    .map(({ a, d }) => ({ ...a, distanceKm: d }) as Airport & { distanceKm: number });
}

export function formatDuration(min: number) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m}m`;
}

export function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const SAVED_ROUTES_KEY = "goh_saved_routes";
const PRICE_ALERTS_KEY = "goh_price_alerts";

export type SavedRoute = { id: string; from: string; to: string; label?: string; savedAt: string };
export type PriceAlert = {
  id: string;
  from: string;
  to: string;
  targetUSD: number;
  createdAt: string;
};

function readLocal<T>(key: string): T[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
}
function writeLocal<T>(key: string, v: T[]) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* ignore */
  }
}

export const savedRoutes = {
  list: () => readLocal<SavedRoute>(SAVED_ROUTES_KEY),
  add: (r: Omit<SavedRoute, "id" | "savedAt">) => {
    const list = readLocal<SavedRoute>(SAVED_ROUTES_KEY);
    const entry: SavedRoute = { ...r, id: crypto.randomUUID(), savedAt: new Date().toISOString() };
    writeLocal(SAVED_ROUTES_KEY, [entry, ...list]);
    return entry;
  },
  remove: (id: string) =>
    writeLocal(
      SAVED_ROUTES_KEY,
      readLocal<SavedRoute>(SAVED_ROUTES_KEY).filter((r) => r.id !== id),
    ),
};

export const priceAlerts = {
  list: () => readLocal<PriceAlert>(PRICE_ALERTS_KEY),
  add: (r: Omit<PriceAlert, "id" | "createdAt">) => {
    const list = readLocal<PriceAlert>(PRICE_ALERTS_KEY);
    const entry: PriceAlert = {
      ...r,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
    };
    writeLocal(PRICE_ALERTS_KEY, [entry, ...list]);
    return entry;
  },
  remove: (id: string) =>
    writeLocal(
      PRICE_ALERTS_KEY,
      readLocal<PriceAlert>(PRICE_ALERTS_KEY).filter((r) => r.id !== id),
    ),
};

export const AIRPORT_GUIDES: {
  code: string;
  wifi: string;
  lounges: number;
  sim: string;
  transitToCity: string;
  tips: string;
}[] = [
  {
    code: "DXB",
    wifi: "Free unlimited",
    lounges: 14,
    sim: "Etisalat/Du kiosks in arrivals",
    transitToCity: "Metro Red Line ~20 min to Downtown",
    tips: "Terminal 3 for Emirates; allow 45 min for transfers.",
  },
  {
    code: "IST",
    wifi: "Free 2h, unlimited with Turkish Airlines",
    lounges: 8,
    sim: "Turkcell / Vodafone in arrivals",
    transitToCity: "M11 metro to Gayrettepe ~35 min",
    tips: "Long walks between gates — wear comfortable shoes.",
  },
  {
    code: "LHR",
    wifi: "Free",
    lounges: 20,
    sim: "WHSmith / vending machines",
    transitToCity: "Elizabeth Line ~35 min to Paddington",
    tips: "Terminals are far apart; use free inter-terminal transit.",
  },
  {
    code: "JFK",
    wifi: "Free",
    lounges: 12,
    sim: "T-Mobile / AT&T booths",
    transitToCity: "AirTrain + LIRR/Subway ~50 min",
    tips: "Allow 2h for US immigration on arrival.",
  },
  {
    code: "SIN",
    wifi: "Free",
    lounges: 10,
    sim: "Singtel/Starhub kiosks",
    transitToCity: "MRT ~40 min to city",
    tips: "Jewel Changi worth a visit even in transit.",
  },
  {
    code: "DOH",
    wifi: "Free",
    lounges: 9,
    sim: "Ooredoo / Vodafone",
    transitToCity: "Metro Gold Line ~20 min",
    tips: "Free city tour available on long layovers.",
  },
];

export const VISA_TRANSIT: {
  country: string;
  requirement: string;
  transitHours: number;
  notes: string;
}[] = [
  {
    country: "United Arab Emirates",
    requirement: "Visa on arrival for many nationalities",
    transitHours: 24,
    notes: "Airside transit at DXB/AUH without a visa is generally allowed.",
  },
  {
    country: "Türkiye",
    requirement: "eVisa for many nationalities",
    transitHours: 24,
    notes: "Airside transit at IST allowed without visa.",
  },
  {
    country: "Qatar",
    requirement: "Visa on arrival for many nationalities",
    transitHours: 24,
    notes: "Free transit visa for stays 5–96 hours.",
  },
  {
    country: "United Kingdom",
    requirement: "Visa required for many nationalities",
    transitHours: 24,
    notes: "Direct Airside Transit Visa (DATV) may be required.",
  },
  {
    country: "United States",
    requirement: "Visa or ESTA required",
    transitHours: 0,
    notes: "No airside transit — you must clear immigration.",
  },
  {
    country: "Schengen Area",
    requirement: "Schengen visa for many nationalities",
    transitHours: 24,
    notes: "Airport Transit Visa may apply for certain nationalities.",
  },
];
